// Formatting helpers shared by pages and exports. All user-visible output is Slovenian.
import type { DocType, DocumentFull, Speaker } from '../../../shared/types';

export const TIME_ZONE = 'Europe/Ljubljana';

const pad2 = (n: number) => String(n).padStart(2, '0');

/** 65_000 -> "1:05", 3_725_000 -> "1:02:05" */
export function formatDuration(ms: number | null | undefined): string {
  if (ms == null || !Number.isFinite(ms) || ms < 0) return '0:00';
  const total = Math.floor(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return h > 0 ? `${h}:${pad2(m)}:${pad2(s)}` : `${m}:${pad2(s)}`;
}

/** Always with hours, for totals: 45_000 -> "0:00:45", 45_240_000 -> "12:34:00". */
export function formatHours(ms: number | null | undefined): string {
  const total = ms == null || !Number.isFinite(ms) || ms < 0 ? 0 : Math.round(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return `${h}:${pad2(m)}:${pad2(s)}`;
}

/** Offset inside a recording, e.g. "02:05" or "1:02:05". */
export function formatOffset(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return h > 0 ? `${h}:${pad2(m)}:${pad2(s)}` : `${pad2(m)}:${pad2(s)}`;
}

const dateTimeFmt = new Intl.DateTimeFormat('sl-SI', {
  timeZone: TIME_ZONE,
  day: 'numeric',
  month: 'numeric',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});
const dateFmt = new Intl.DateTimeFormat('sl-SI', {
  timeZone: TIME_ZONE,
  day: 'numeric',
  month: 'numeric',
  year: 'numeric',
});
const longDateFmt = new Intl.DateTimeFormat('sl-SI', {
  timeZone: TIME_ZONE,
  weekday: 'long',
  day: 'numeric',
  month: 'long',
  year: 'numeric',
});
const timeFmt = new Intl.DateTimeFormat('sl-SI', {
  timeZone: TIME_ZONE,
  hour: '2-digit',
  minute: '2-digit',
});

/** "24. 10. 2026, 14:05" */
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : dateTimeFmt.format(d);
}

/** "24. 10. 2026" */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : dateFmt.format(d);
}

/** "sobota, 24. oktober 2026" */
export function formatLongDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : longDateFmt.format(d);
}

/** "14:05" */
export function formatTime(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : timeFmt.format(d);
}

/** Calendar day (YYYY-MM-DD) of an instant in Ljubljana time. */
export function localDayKey(d: Date): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(d);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')}`;
}

/** Human friendly due date: "danes ob 10:00", "jutri ob 9:30", "v petek ob 8:00", "24. 10. 2026 ob 10:00". */
export function formatDue(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const time = formatTime(iso);
  const dayMs = 86_400_000;
  const today = localDayKey(now);
  if (localDayKey(d) === today) return `danes ob ${time}`;
  if (localDayKey(new Date(now.getTime() + dayMs)) === localDayKey(d)) return `jutri ob ${time}`;
  if (localDayKey(new Date(now.getTime() - dayMs)) === localDayKey(d)) return `včeraj ob ${time}`;
  const diffDays = (d.getTime() - now.getTime()) / dayMs;
  if (diffDays > 0 && diffDays < 6) {
    const weekday = new Intl.DateTimeFormat('sl-SI', { timeZone: TIME_ZONE, weekday: 'long' }).format(d);
    // Accusative: sreda -> sredo, sobota -> soboto, nedelja -> nedeljo; the other days are unchanged.
    return `v ${weekday.replace(/a$/, 'o')} ob ${time}`;
  }
  return `${formatDate(iso)} ob ${time}`;
}

/**
 * Short due time for one-line lists: "danes 10:00", "jutri 09:00", "čet 08:00" (within six days),
 * "15. 11. 09:00" (this year), "15. 11. 2027 09:00"; the past the same way ("včeraj 14:00").
 */
export function formatDueShort(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const time = formatTime(iso);
  const dayMs = 86_400_000;
  const day = localDayKey(d);
  if (day === localDayKey(now)) return `danes ${time}`;
  if (day === localDayKey(new Date(now.getTime() + dayMs))) return `jutri ${time}`;
  if (day === localDayKey(new Date(now.getTime() - dayMs))) return `včeraj ${time}`;
  const diffDays = (d.getTime() - now.getTime()) / dayMs;
  if (diffDays > 0 && diffDays < 6) {
    const weekday = new Intl.DateTimeFormat('sl-SI', { timeZone: TIME_ZONE, weekday: 'short' }).format(d).replace(/\.$/, '');
    return `${weekday} ${time}`;
  }
  const parts = new Intl.DateTimeFormat('en-GB', { timeZone: TIME_ZONE, day: 'numeric', month: 'numeric', year: 'numeric' }).formatToParts(d);
  const get = (t: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === t)?.value ?? '';
  const sameYear = get('year') === localDayKey(now).slice(0, 4);
  return `${Number(get('day'))}. ${Number(get('month'))}.${sameYear ? '' : ` ${get('year')}`} ${time}`;
}

export const DOC_TYPE_LABEL: Record<DocType, string> = {
  meeting: 'Sestanek',
  inspection: 'Nadzor',
  note: 'Opomnik',
};

/** Default title for a new document, e.g. "Sestanek 24. 10. 2026". */
export function defaultTitle(type: DocType, at: Date = new Date()): string {
  return `${DOC_TYPE_LABEL[type]} ${formatDate(at.toISOString())}`;
}

/** "S3" -> "Govorec 3" */
export function defaultSpeakerName(key: string): string {
  const n = /^S(\d+)$/.exec(key)?.[1];
  return n ? `Govorec ${n}` : key;
}

export function speakerName(doc: Pick<DocumentFull, 'speakers'>, key: string | null): string {
  if (!key) return '';
  return doc.speakers.find((s: Speaker) => s.key === key)?.name || defaultSpeakerName(key);
}

const SPEAKER_COLORS = ['#2563eb', '#db2777', '#16a34a', '#ea580c', '#7c3aed', '#0891b2', '#ca8a04', '#dc2626'];

/** Stable colour per speaker key. */
export function speakerColor(key: string | null): string {
  if (!key) return '#64748b';
  const n = Number(/^S(\d+)$/.exec(key)?.[1] ?? 0);
  return SPEAKER_COLORS[(Math.max(1, n) - 1) % SPEAKER_COLORS.length]!;
}

/** Slovenian plural forms: plural(3, ['posnetek', 'posnetka', 'posnetki', 'posnetkov']) -> "3 posnetki". */
export function plural(n: number, forms: [one: string, two: string, few: string, many: string]): string {
  const mod100 = Math.abs(n) % 100;
  const form = mod100 === 1 ? forms[0] : mod100 === 2 ? forms[1] : mod100 === 3 || mod100 === 4 ? forms[2] : forms[3];
  return `${n} ${form}`;
}

/** File-name friendly slug that keeps Slovenian letters readable: "Nadzor Šola" -> "Nadzor-Sola". */
export function slugify(s: string): string {
  return (
    s
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^A-Za-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 60) || 'dokument'
  );
}
